import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  InputAdornment,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme } from
'@mui/material';
import {
  Search as SearchIcon,
  ExpandLess,
  ExpandMore,
  LocationOn,
  Business,
  Layers,
  Room,
  Home,
  Apartment,
  MeetingRoom,
  Devices,
  Clear as ClearIcon,
  ChevronRight } from
'@mui/icons-material';

// Icon mapping for location types
import { useThemeSettings } from "../../contexts/ThemeContext";const LOCATION_TYPE_ICONS = {
  1: LocationOn, // Site
  2: Business, // Building
  3: Layers, // Floor
  4: Home, // Unit
  5: MeetingRoom, // Room
  6: Apartment, // Zone
  7: Room, // Area
  8: LocationOn // Other
};

// Icon mapping for device types
const DEVICE_TYPE_ICONS = {
  default: Devices
};

const TreeNode = ({
  node,
  level = 0,
  onSelect,
  selectedId,
  expandedNodes,
  onToggleExpand,
  searchTerm,
  type = 'location',
  siteName = null
}) => {const { t } = useThemeSettings();
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.includes(node.id || node._id);
  const isSelected = selectedId === (node.id || node._id);

  // Get appropriate icon based on type
  const getIcon = () => {const { t } = useThemeSettings();
    if (type === 'location') {
      const IconComponent = LOCATION_TYPE_ICONS[node.locationType] || LocationOn;
      return <IconComponent fontSize="small" />;
    } else {
      return <Devices fontSize="small" />;
    }
  };

  // Highlight search term in text
  const highlightText = (text) => {const { t } = useThemeSettings();
    if (!searchTerm) return text;

    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return (
      <span>
        {parts.map((part, index) =>
        part.toLowerCase() === searchTerm.toLowerCase() ?
        <mark key={index} style={{ backgroundColor: '#ffeb3b' }}>{part}</mark> :
        part
        )}
      </span>);

  };

  const nodeName = node.friendlyName || node.name || `${type} ${node.id || node._id}`;

  return (
    <>
      <ListItem
        disablePadding
        sx={{
          pl: searchTerm ? 0 : level * 2,
          backgroundColor: isSelected ? 'action.selected' : 'transparent'
        }}>
        
        {hasChildren &&
        <IconButton
          size="small"
          onClick={() => onToggleExpand(node.id || node._id)}
          sx={{ ml: 0.5 }}>
          
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        }
        {!hasChildren && <Box sx={{ width: 36 }} />}

        <ListItemButton
          onClick={() => onSelect(node)}
          selected={isSelected}>
          
          <ListItemIcon sx={{ minWidth: 36 }}>
            {getIcon()}
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <span>{highlightText(nodeName)}</span>
                {searchTerm && siteName && siteName !== nodeName && node.locationType !== 1 && (
                  <>
                    <ChevronRight sx={{ fontSize: 16, color: 'text.disabled' }} />
                    <Chip
                      icon={<LocationOn sx={{ fontSize: 16 }} />}
                      label={siteName}
                      size="small"
                      sx={{
                        height: 22,
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        fontWeight: 500,
                        '& .MuiChip-icon': {
                          color: 'primary.contrastText'
                        }
                      }}
                    />
                  </>
                )}
              </Box>
            }
            secondary={
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                {node.locationType &&
              <Chip
                label={getLocationTypeName(node.locationType)}
                size="small"
                variant="outlined" />

              }
                {node.deviceTypeModel?.friendlyName &&
              <Chip
                label={node.deviceTypeModel.friendlyName}
                size="small"
                variant="outlined" />

              }
                {node.config?.status &&
              <Chip
                label={node.config.status}
                size="small"
                color={node.config.status === 'Registered' ? 'success' : 'default'}
                variant="outlined" />

              }
              </Box>
            } />
          
        </ListItemButton>
      </ListItem>

      {hasChildren &&
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {node.children.map((child) =>
          <TreeNode
            key={child.id || child._id}
            node={child}
            level={level + 1}
            onSelect={onSelect}
            selectedId={selectedId}
            expandedNodes={expandedNodes}
            onToggleExpand={onToggleExpand}
            searchTerm={searchTerm}
            type={type}
            siteName={child.siteName || siteName} />

          )}
          </List>
        </Collapse>
      }
    </>);

};

// Helper function to get location type name
const getLocationTypeName = (typeId) => {
  const types = {
    1: 'Site',
    2: 'Building',
    3: 'Floor',
    4: 'Unit',
    5: 'Room',
    6: 'Zone',
    7: 'Area',
    8: 'Other'
  };
  return types[typeId] || 'Unknown';
};

const TreeSelect = ({
  open,
  onClose,
  onSelect,
  data = [],
  selectedId = null,
  title = 'Select Item',
  type = 'location', // 'location' or 'device'
  loading = false,
  error = null,
  allowNone = false
}) => {const { t } = useThemeSettings();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);

  // Build tree structure from flat data if needed
  const treeData = useMemo(() => {
    if (!Array.isArray(data)) return [];

    // Check if data is already hierarchical
    if (data.length > 0 && data[0].children !== undefined) {
      return data;
    }

    // Build hierarchy from flat list
    const nodeMap = new Map();
    const roots = [];

    // First pass: create all nodes
    data.forEach((item) => {
      const id = item.id || item._id || (type === 'device' ? item.device_UUID : item.location_UUID);
      nodeMap.set(id, { ...item, children: [] });
    });

    // Second pass: build hierarchy
    data.forEach((item) => {
      const id = item.id || item._id || (type === 'device' ? item.device_UUID : item.location_UUID);
      const parentId = item.parentLocation || item.parent_id || item.parentId || item.locationId;
      const node = nodeMap.get(id);

      if (parentId && nodeMap.has(parentId)) {
        nodeMap.get(parentId).children.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots;
  }, [data, type]);

  // Filter tree based on search term
  const filteredTree = useMemo(() => {
    if (!searchTerm) return treeData;

    // Flatten the tree and find all matching nodes
    const flattenAndFilter = (nodes, siteName = null) => {
      let matches = [];

      nodes.forEach((node) => {
        // Determine the site name for this branch
        const currentSiteName = node.locationType === 1
          ? (node.friendlyName || node.name)
          : siteName;

        const nodeName = node.friendlyName || node.name || '';

        // Create a combined search string with both node name and site name
        const searchableText = `${nodeName} ${currentSiteName || ''}`.toLowerCase();

        // Split search term by spaces and check if all parts match
        const searchParts = searchTerm.toLowerCase().split(/\s+/).filter(part => part.length > 0);
        const matchesSearch = searchParts.every(part => searchableText.includes(part));

        // If this node matches, add it to results
        if (matchesSearch) {
          matches.push({ ...node, children: [], siteName: currentSiteName });
        }

        // Recursively search children
        if (node.children && node.children.length > 0) {
          matches = matches.concat(flattenAndFilter(node.children, currentSiteName));
        }
      });

      return matches;
    };

    return flattenAndFilter(treeData);
  }, [treeData, searchTerm]);

  // Auto-expand nodes when not searching (clear expanded nodes when searching)
  // On mobile, keep tree collapsed unless user is searching
  useEffect(() => {
    if (searchTerm) {
      // Clear expanded nodes when searching (we show flat list)
      setExpandedNodes([]);
    } else if (isMobile) {
      // On mobile, keep tree collapsed when not searching
      setExpandedNodes([]);
    } else {
      // On desktop, reset expanded nodes when not searching
      setExpandedNodes([]);
    }
  }, [searchTerm, isMobile]);

  const handleToggleExpand = (nodeId) => {
    setExpandedNodes((prev) =>
    prev.includes(nodeId) ?
    prev.filter((id) => id !== nodeId) :
    [...prev, nodeId]
    );
  };

  const handleNodeSelect = (node) => {
    setSelectedNode(node);

    // On mobile, collapse tree and clear search after selection
    if (isMobile) {
      setExpandedNodes([]);
      setSearchTerm('');
    }
  };

  const handleConfirm = () => {
    if (selectedNode) {
      onSelect(selectedNode);
    } else if (allowNone) {
      onSelect(null);
    }

    // Clear state when closing
    setSearchTerm('');
    setExpandedNodes([]);
    setSelectedNode(null);

    onClose();
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleClose = () => {
    // Clear state when closing
    setSearchTerm('');
    setExpandedNodes([]);
    setSelectedNode(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}>
      
      <DialogTitle>
        {title}
        <TextField
          fullWidth
          variant="outlined"
          placeholder={`Search ${type}s...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mt: 2 }}
          InputProps={{
            startAdornment:
            <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>,

            endAdornment: searchTerm &&
            <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>

          }} />
        
      </DialogTitle>

      <DialogContent dividers>
        {loading &&
        <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        }

        {error &&
        <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        }

        {!loading && !error && filteredTree.length === 0 &&
        <Typography color="text.secondary" align="center" py={3}>
            {searchTerm ? `No ${type}s found matching "${searchTerm}"` : `No ${type}s available`}
          </Typography>
        }

        {!loading && !error && filteredTree.length > 0 &&
        <List>
            {allowNone &&
          <ListItemButton
            onClick={() => handleNodeSelect(null)}
            selected={selectedNode === null}>
            
                <ListItemText
              primary="None"
              secondary={`No parent ${type}`} />
            
              </ListItemButton>
          }
            {filteredTree.map((node) =>
          <TreeNode
            key={node.id || node._id}
            node={node}
            onSelect={handleNodeSelect}
            selectedId={selectedNode?.id || selectedNode?._id}
            expandedNodes={expandedNodes}
            onToggleExpand={handleToggleExpand}
            searchTerm={searchTerm}
            type={type}
            siteName={node.siteName} />

          )}
          </List>
        }

        {selectedNode &&
        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>{t ? t("TreeSelect.selected") : "Selected:"}
            {selectedNode.friendlyName || selectedNode.name}
            </Typography>
            {selectedNode.locationType &&
          <Typography variant="body2" color="text.secondary">{t ? t("DeviceStatusDashboard.type") : "Type:"}
            {getLocationTypeName(selectedNode.locationType)}
              </Typography>
          }
          </Box>
        }
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{t ? t("LocationDialog.cancel") : "Cancel"}</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedNode && !allowNone}>{t ? t("TreeSelect.select") : "Select"}


        </Button>
      </DialogActions>
    </Dialog>);

};

export default TreeSelect;